import React, { useState } from 'react';
import { Save, Play, X, Flag, Radiation, FolderOpen } from 'lucide-react';
import { playConsoleClick } from '../utils/sounds';

export default function PauseMenu({ game, onSave, onContinue, onClose, onSurrender, onLoadGame }) {
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  
  const player = game.countries.find(c => c.is_player);
  const currentEnrichment = player?.resources?.uranium_enrichment || 0;
  const playerMoney = player?.resources?.money || 0;
  
  // Enrichment purchase option (only the $1B for 25% option)
  const enrichmentOption = { amount: 25, cost: 1000, label: '+25%', color: 'purple' };
  
  const handlePurchaseEnrichment = async (amount, cost) => {
    if (purchasing) return;
    
    // Check if player has enough money
    if (playerMoney < cost) {
      setError(`Insufficient funds! Need $${cost}M, have $${playerMoney}M`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    // Check if enrichment would exceed 100%
    if (currentEnrichment >= 100) {
      setError('Uranium enrichment already at 100%!');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    setPurchasing(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/games/${game.id}/purchase-enrichment?amount=${amount}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // ⭐ FIX: Read JSON only once
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to purchase enrichment');
      }
      
      // Update game state (this will trigger a re-render with new values)
      window.location.reload(); // Simple refresh to update all game state
      
    } catch (error) {
      console.error('Purchase enrichment error:', error);
      setError(error.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setPurchasing(false);
    }
  };
  
  const handleSave = async () => {
    await onSave();
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-md pointer-events-auto"
      onClick={(e) => {
        // Only close if clicking the backdrop (not the menu itself)
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div 
        className="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 border-4 border-blue-600 rounded-lg p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">⏸️</div>
          <h2 className="text-3xl font-bold text-blue-400 mb-2">
            GAME PAUSED
          </h2>
          <p className="text-gray-400">
            {game.name} - Turn {game.current_turn}
          </p>
        </div>

        {/* Game Info */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="text-sm text-gray-300 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Your Country:</span>
              <span className="font-bold text-white">
                {game.countries.find(c => c.is_player)?.name || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Turn:</span>
              <span className="font-bold text-white">{game.current_turn}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Actions Remaining:</span>
              <span className="font-bold text-white">
                {game.countries.find(c => c.is_player)?.actions_remaining || 0} / 3
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Money:</span>
              <span className="font-bold text-green-400">${playerMoney}M</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400 flex items-center gap-1">
                <Radiation className="w-3 h-3 text-yellow-400" />
                Enrichment:
              </span>
              <span className="font-bold text-yellow-400">{currentEnrichment.toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {/* Enrichment Purchase Section */}
        <div className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-2 border-yellow-600/40 rounded-lg p-4 mb-6">
          <div className="text-center mb-3">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Radiation className="w-5 h-5 text-yellow-400" />
              <h3 className="text-lg font-bold text-yellow-400">PURCHASE URANIUM ENRICHMENT</h3>
            </div>
            <p className="text-xs text-gray-400">Accelerate your nuclear program</p>
          </div>
          
          {error && (
            <div className="bg-red-900/50 border border-red-500 rounded p-2 mb-3 text-center text-sm text-red-200">
              ⚠️ {error}
            </div>
          )}
          
          {/* Single purchase button - centered */}
          <div className="flex justify-center">
            {(() => {
              const option = enrichmentOption;
              const canAfford = playerMoney >= option.cost;
              const wouldExceed = currentEnrichment + option.amount > 100;
              const isDisabled = !canAfford || wouldExceed || purchasing || currentEnrichment >= 100;
              
              return (
                <button
                  onClick={() => {
                    playConsoleClick(0.5);
                    handlePurchaseEnrichment(option.amount, option.cost);
                  }}
                  disabled={isDisabled}
                  className={`
                    relative py-4 px-8 rounded-lg font-bold text-lg transition-all w-64
                    ${isDisabled 
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed opacity-50' 
                      : 'bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white transform hover:scale-105 shadow-lg'
                    }
                  `}
                >
                  <div className="text-2xl mb-1">{option.label}</div>
                  <div className="text-sm opacity-90">${option.cost}M</div>
                  {!canAfford && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                      <span className="text-sm text-red-400">💰 Need ${option.cost}M</span>
                    </div>
                  )}
                </button>
              );
            })()}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Continue Playing */}
          <button
            onClick={() => {
              playConsoleClick();
              onContinue();
            }}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all flex items-center justify-center gap-3"
          >
            <Play className="w-5 h-5" />
            CONTINUE PLAYING
          </button>

          {/* Save & Exit */}
          <button
            onClick={() => {
              playConsoleClick();
              handleSave();
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all flex items-center justify-center gap-3"
          >
            <Save className="w-5 h-5" />
            SAVE & EXIT
          </button>

          {/* Load Game */}
          <button
            onClick={() => {
              playConsoleClick();
              if (window.confirm('⚠️ Load your last saved game?\n\nAny unsaved progress will be lost!')) {
                onLoadGame();
              }
            }}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all flex items-center justify-center gap-3"
          >
            <FolderOpen className="w-5 h-5" />
            LOAD LAST SAVE
          </button>

          {/* Surrender */}
          <button
            onClick={() => {
              playConsoleClick();
              if (window.confirm('⚠️ Are you sure you want to SURRENDER?\n\nThis will end your game and you will be defeated!')) {
                onSurrender();
              }
            }}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all flex items-center justify-center gap-3"
          >
            <Flag className="w-5 h-5" />
            SURRENDER
          </button>

          {/* Close (ESC) */}
          <button
            onClick={() => {
              playConsoleClick(0.3);
              onClose();
            }}
            className="w-full bg-gray-700 hover:bg-gray-600 text-gray-300 font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            <span className="text-sm">Press ESC to close</span>
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Your game progress is automatically saved</p>
          <p className="mt-1">Press <kbd className="px-2 py-1 bg-gray-800 rounded">ESC</kbd> anytime to pause</p>
        </div>
      </div>
    </div>
  );
}
